import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistStorage } from '@/stores/persistStorage';
import { toDateKey } from '@/lib/dateKey';
import type { ChatTurn } from '@/features/ai/useCommand';

export type ActionOutcomeState = 'done' | 'cancelled';

export interface AskThreadState {
  messages: ChatTurn[];
  dayKey: string;
  /** Per-action outcome, keyed "messageIndex:actionIndex". Persisted so an
   * applied/cancelled action card stays settled after the sheet is closed and
   * reopened (the messages persist, so the outcomes must too). Reset whenever
   * the thread is cleared/replaced or the day rolls over. */
  outcomes: Record<string, ActionOutcomeState>;
  /** Append one turn; resets to a fresh thread if the stored day rolled over. */
  append: (turn: ChatTurn) => void;
  /** Replace the whole thread and stamp today. */
  setThread: (messages: ChatTurn[]) => void;
  /** Empty the thread and re-stamp today. */
  clear: () => void;
  /** Record that an action card was applied ('done') or dismissed ('cancelled'). */
  markOutcome: (key: string, state: ActionOutcomeState) => void;
  /** Revert an action card to pending (e.g. on Undo). */
  clearOutcome: (key: string) => void;
}

export const useAskThreadStore = create<AskThreadState>()(
  persist(
    (set) => ({
      messages: [],
      dayKey: toDateKey(new Date()),
      outcomes: {},
      append: (turn) =>
        set((s) => {
          const today = toDateKey(new Date());
          return s.dayKey === today
            ? { messages: [...s.messages, turn] }
            : { messages: [turn], dayKey: today, outcomes: {} };
        }),
      setThread: (messages) => set({ messages, dayKey: toDateKey(new Date()), outcomes: {} }),
      clear: () => set({ messages: [], dayKey: toDateKey(new Date()), outcomes: {} }),
      markOutcome: (key, state) => set((s) => ({ outcomes: { ...s.outcomes, [key]: state } })),
      clearOutcome: (key) =>
        set((s) => {
          if (!(key in s.outcomes)) return {};
          const next = { ...s.outcomes };
          delete next[key];
          return { outcomes: next };
        }),
    }),
    {
      name: 'brainday:ask-thread',
      storage: persistStorage<AskThreadState>(),
      partialize: (s) =>
        ({ messages: s.messages, dayKey: s.dayKey, outcomes: s.outcomes }) as AskThreadState,
      // Prune a previous day's thread as it hydrates (correct for async native storage too).
      merge: (persisted, current) => {
        const p = persisted as Partial<AskThreadState> | undefined;
        const today = toDateKey(new Date());
        if (p?.dayKey !== today) return { ...current, messages: [], dayKey: today, outcomes: {} };
        return {
          ...current,
          messages: p.messages ?? [],
          dayKey: p.dayKey,
          outcomes: p.outcomes ?? {},
        };
      },
    },
  ),
);
